/**
 * @file storageBackup.js
 * @location src/utils/storageBackup.js
 * @role JSON Export/Import — 사용자 수동 백업·복원
 *
 * exportToJSON(state)   → 영속 필드를 JSON 파일로 다운로드
 * importFromJSON(file)  → 사용자 선택 파일 읽고 검증·마이그레이션 → Promise<영속객체>
 *
 * 영속 키 화이트리스트·SCHEMA_VERSION 은 storage.js 와 동일 — 변경 시 양쪽 동기 필요.
 */
import { migrate } from './migrations.js';
import { safeJsonParse, PERSISTENT_KEYS, SCHEMA_VERSION, isPlainObject } from './storage.js';

/**
 * JSON 백업 — 영속 필드를 JSON 파일로 다운로드.
 */
export async function exportToJSON(state) {
  const payload = { _version: SCHEMA_VERSION };
  PERSISTENT_KEYS.forEach(k => { payload[k] = state[k]; });

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const date = new Date().toISOString().slice(0, 10);
  const a = document.createElement('a');
  a.href = url;
  a.download = `workdo_backup_${date}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function importFromJSON(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('파일 불러오기에 실패했습니다'));
    reader.onload = async (e) => {
      const data = safeJsonParse(e.target.result, 'storage.import');
      if (data == null) return reject(new Error('JSON 파싱에 실패했습니다'));

      if (!isPlainObject(data))                           return reject(new Error('JSON 형식이 올바르지 않습니다 (객체가 아닙니다)'));
      if (!Array.isArray(data.tasks))                     return reject(new Error('tasks 배열을 찾을 수 없습니다'));

      try {
        const migrated = (data._version ?? 0) < SCHEMA_VERSION ? migrate(data) : data;
        // _version 은 reducer 상태로 흘려보내지 않는다
        const { _version: _v, ...rest } = migrated;
        resolve(rest);
      } catch (err) {
        reject(err instanceof Error ? err : new Error('마이그레이션에 실패했습니다'));
      }
    };
    reader.readAsText(file);
  });
}
